import { type DashboardReady } from "./useDashboardController";
import { ClipboardCheck, Gavel, FileText } from "lucide-react";
import { Link } from "wouter";
import { peso } from "./model";
import { engagementStatusLabel } from "../contracts/engagementList";

export function DashboardWorkActivity(props: DashboardReady) {
  const { engagements, t, isMobileViewport, showAllEngagements, setShowAllEngagements, myBids, showAllBids, setShowAllBids, myQuotes, showAllQuotes, setShowAllQuotes } = props;

  return (<>
        {/* v3.9.19 — post-hire engagements (work monitoring) */}
        {engagements.length > 0 && (
          <div id="sec-engagements" data-mobile-section="work" className="card p-5 mt-4 scroll-mt-20 dashboard-section">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy">{t("dashboard.engagements.title")}</h3>
              <span className="text-xs text-mut">{t("dashboard.engagements.description")}</span>
            </div>
            <div className="space-y-2">
              {(isMobileViewport || showAllEngagements ? engagements : engagements.slice(0, 5)).map((e) => {
                const pct = e.tasksTotal ? Math.round((e.tasksDone / e.tasksTotal) * 100) : 0;
                return (
                  <Link key={e.id} href={`/engagement/${encodeURIComponent(e.id)}`} className="block border border-line rounded-lg p-3 hover:border-navy-500">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-navy min-w-0 truncate"><span className="font-medium">{e.jobTitle}</span><span className="text-mut"> · {e.role === "employer" ? "worker" : "employer"}: {e.counterpartName}</span></span>
                      <span className="text-xs whitespace-nowrap ml-3">
                        {e.paymentRequired ? <span className="chip bg-navy-50 text-navy">{engagementStatusLabel(e)}</span> : <>
                        {e.status === "completed" && <span className="chip bg-brand-green/10 text-brand-greendark">{t("engagement.completed")}</span>}
                        {e.status === "cancelled" && <span className="chip bg-line text-mut">{t("engagement.cancelled")}</span>}
                        {e.status === "active" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">{t("engagement.active")}</span>}
                        </>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="h-1.5 flex-1 rounded-full bg-navy-50 overflow-hidden"><div className="h-full bg-brand-green" style={{ width: `${pct}%` }} /></div>
                      <span className="text-[11px] text-mut whitespace-nowrap">{e.tasksDone}/{e.tasksTotal} tasks</span>
                    </div>
                  </Link>
                );
              })}
            </div>
            {!isMobileViewport && engagements.length > 5 && <div className="mt-3 flex justify-end"><button type="button" onClick={() => setShowAllEngagements((shown) => !shown)} aria-expanded={showAllEngagements} className="text-sm font-semibold text-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{showAllEngagements ? t("common.showLess", "Show less") : t("common.showMore", "Show more")}</button></div>}
          </div>
        )}

        {/* My Bids — Increment 3 */}
        {myBids && myBids.length > 0 && (
          <div id="sec-bids" data-mobile-section="work" className="card p-5 mt-4 scroll-mt-20 dashboard-section">
            <div className="flex items-center gap-2 mb-3">
              <Gavel className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy shrink-0 whitespace-nowrap">{t("dashboard.nav.myBids")}</h3>
              <span className="text-xs text-mut min-w-0">Your sealed prices are visible only to you and the project owner.</span>
            </div>
            <div className="space-y-2">
              {(showAllBids ? myBids : myBids.slice(0, 5)).map((b) => (
                <Link key={b.id} href={`/project/${b.projectId}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                  <span className="text-sm text-navy min-w-0">
                    <span className="font-medium">{b.projectTitle || b.projectId}</span>
                    <span className="text-mut"> · {peso(b.status === "awarded" && b.finalPrice != null ? b.finalPrice : b.price)}{b.durationText ? ` · ${b.durationText}` : ""}</span>
                  </span>
                  <span className="text-xs whitespace-nowrap ml-3">
                    {b.status === "awarded" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">{t("activity.status.awarded")}</span>}
                    {b.status === "in_negotiation" && <span className="chip bg-brand-gold/10 text-[#8a5a00]">Negotiating</span>}
                    {b.status === "shortlisted" && <span className="chip bg-brand-green/10 text-brand-greendark">{t("activity.status.shortlisted")}</span>}
                    {b.status === "submitted" && <span className="chip bg-navy-50 text-navy-600">{t("bid.submitted")}</span>}
                    {b.status === "rejected" && <span className="chip bg-line text-mut">Not selected</span>}
                  </span>
                </Link>
              ))}
            </div>
            {myBids.length > 5 && <div className="mt-3 flex justify-end"><button type="button" onClick={() => setShowAllBids((shown) => !shown)} aria-expanded={showAllBids} className="text-sm font-semibold text-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{showAllBids ? "Show less" : "Show more"}</button></div>}
          </div>
        )}

        {/* My Quotes — Increment 7 */}
        {myQuotes && myQuotes.length > 0 && (
          <div data-mobile-section="work" className="card p-5 mt-4 dashboard-section">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy">{t("dashboard.myQuotes")}</h3>
              <span className="text-xs text-mut">Your sealed quote totals, visible only to you and the buyer.</span>
            </div>
            <div className="space-y-2">
              {(showAllQuotes ? myQuotes : myQuotes.slice(0, 5)).map((q) => (
                <Link key={q.id} href={`/rfq/${q.rfqId}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                  <span className="text-sm text-navy min-w-0">
                    <span className="font-medium">{q.rfqTitle || q.rfqId}</span>
                    <span className="text-mut"> · {peso(q.total)}</span>
                  </span>
                  <span className="text-xs whitespace-nowrap ml-3">
                    {q.status === "awarded" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">{t("activity.status.awarded")}</span>}
                    {q.status === "shortlisted" && <span className="chip bg-brand-green/10 text-brand-greendark">{t("activity.status.shortlisted")}</span>}
                    {q.status === "submitted" && <span className="chip bg-navy-50 text-navy-600">{t("bid.submitted")}</span>}
                    {q.status === "rejected" && <span className="chip bg-line text-mut">Not selected</span>}
                  </span>
                </Link>
              ))}
            </div>
            {myQuotes.length > 5 && <div className="mt-3 flex justify-end"><button type="button" onClick={() => setShowAllQuotes((shown) => !shown)} aria-expanded={showAllQuotes} className="text-sm font-semibold text-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{showAllQuotes ? "Show less" : "Show more"}</button></div>}
          </div>
        )}

        </>);
}
